/**
 * Phase 41 Plan 41-16 Task 1 — RecentTimesheetsCard.
 *
 * Bottom-row right card on the v7 Personal Dashboard (per CONTEXT amendment
 * D-Dashboard-layout-v3, 2026-05-12). Shows the user's last 4 monthly
 * timesheets as horizontal progress bars with status chips.
 *
 * Data source: `getTimesheetHistory(limit)` from `services/api/timeEntry.ts` —
 * returns the user's own timesheet rows (state + period + totalHours). Wrapped
 * in TanStack Query directly here (no shared hook exists) so the
 * 5-minute staleTime keeps refetch storms in check on dashboard navigation.
 *
 * T-41-V3-03 mitigation — period.name is server-formatted; rendered via React
 * default escaping. No `dangerouslySetInnerHTML`.
 *
 * Quick task 260929-bq9 (MKS-179, 2026-09-29) — the flat 160h constant below
 * disagreed with the holiday-aware target used elsewhere and was applied
 * identically to every row regardless of which period it covered. Replaced
 * with a per-row target computed via the shared `getMonthlyTargetHours`
 * utility. Rows can span a year boundary (e.g. December + January), so
 * holidays are fetched for every distinct year present among the loaded
 * rows via a single multi-year query, rather than one `getHolidays` call.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card } from '../ui/Card';
import { Pill } from '../ui/Pill';
import { getTimesheetHistory, getHolidays } from '../../services/api/timeEntry';
import { getMonthlyTargetHours } from '../../utils/workingHours';

const RECENT_LIMIT = 4;

function statePill(state: string) {
  const norm = state.toLowerCase();
  if (norm === 'approved') {
    return (
      <Pill bg="#DCFCE7" fg="#166534">
        Approved
      </Pill>
    );
  }
  if (norm === 'submitted') {
    return (
      <Pill bg="#FEF3C7" fg="#92400E">
        Submitted
      </Pill>
    );
  }
  if (norm === 'rejected') {
    return (
      <Pill bg="#FFE4E6" fg="#BE123C">
        Rejected
      </Pill>
    );
  }
  return (
    <Pill bg="#E0F2FE" fg="#075985">
      Open
    </Pill>
  );
}

export function RecentTimesheetsCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['dashboard', 'recentTimesheets', RECENT_LIMIT],
    queryFn: () => getTimesheetHistory(RECENT_LIMIT),
    staleTime: 5 * 60 * 1000,
  });

  const rows = (data?.data ?? []).slice(0, RECENT_LIMIT);

  // Distinct years present among the loaded rows (rows can span a year
  // boundary, e.g. December + January).
  const years = useMemo(() => {
    const yearSet = new Set<number>();
    for (const row of rows) {
      yearSet.add(Number(row.period.from.split('-')[0]));
    }
    return Array.from(yearSet).sort((a, b) => a - b);
  }, [rows]);

  const { data: holidaysData } = useQuery({
    queryKey: ['holidays', 'multi', years],
    queryFn: () => Promise.all(years.map((y) => getHolidays(y))),
    staleTime: 24 * 60 * 60 * 1000, // Cache for 24 hours
    enabled: years.length > 0,
  });

  const holidayDates = new Set<string>();
  if (holidaysData) {
    for (const response of holidaysData) {
      for (const holiday of response.data) {
        holidayDates.add(holiday.date);
      }
    }
  }

  return (
    <Card
      title="Recent timesheets"
      action={
        <Link to="/time" className="text-[11.5px] text-ms-orange-dark hover:underline">
          View all →
        </Link>
      }
    >
      {isLoading ? (
        <div className="text-[12px] text-ms-text-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <p className="text-[12px] text-ms-text-muted">No timesheets yet.</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => {
            // Per-row target (not global): parse this row's own period so
            // each row reflects the month it actually covers.
            const [rowYearStr, rowMonthStr] = row.period.from.split('-');
            const rowYear = Number(rowYearStr);
            const rowMonthIndex = Number(rowMonthStr) - 1;
            const rowTarget = getMonthlyTargetHours(rowYear, rowMonthIndex, holidayDates);

            const pct = Math.min(100, Math.round(((row.totalHours ?? 0) / rowTarget) * 100));
            return (
              <li key={row.id} className="space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-semibold text-ms-text-strong truncate">
                      {row.period.name}
                    </div>
                    <div className="text-[10.5px] text-ms-text-muted tabular-nums">
                      {row.totalHours.toFixed(1)}h logged · target {rowTarget}h
                    </div>
                  </div>
                  {statePill(row.state)}
                </div>
                <div
                  className="h-1.5 rounded-full bg-ms-surface-2 overflow-hidden"
                  role="progressbar"
                  aria-label={`${row.totalHours.toFixed(1)} hours logged of ${rowTarget}`}
                  aria-valuenow={Math.round(row.totalHours)}
                  aria-valuemin={0}
                  aria-valuemax={rowTarget}
                >
                  <div
                    className="h-full bg-ms-orange"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
